"use client";

import React from 'react';
import Link from 'next/link';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';

export default function TermsOfServicePage() {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body">
      <LandingHeader transparent />
      
      <main className="flex-1">
        <section className="pt-32 pb-20 px-6">
          <div className="container mx-auto max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl mb-4">Terms of Service</h1>
            <p className="text-muted-foreground mb-12">Last updated: 19 December 2025</p>
            
            <div className="prose prose-slate max-w-none space-y-8 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the Willdone website (the &ldquo;Website&rdquo;) and any services provided through it (the &ldquo;Services&rdquo;).
              </p>
              
              <p>
                By using the Website or purchasing Services, you agree to these Terms. If you do not agree, do not use the Website or Services.
              </p>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">1. About Willdone and how to contact us</h2>
                <p>
                  Willdone (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is an online will-writing website for people creating wills intended for England and Wales.
                </p>
                <p>
                  <strong>Contact email:</strong> hello@willdone.co.uk
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">2. Who the Services are for</h2>
                <p>
                  2.1 The Services are intended for individuals aged 18 or over who have the legal capacity to make a will.
                </p>
                <p>
                  2.2 The Services are designed for wills intended to be used in England and Wales. If you have significant overseas connections (assets, residence, domicile, etc.), you should seek independent legal advice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">3. What we provide (and what we do not)</h2>
                <p>
                  3.1 We provide an online service to generate will documents and related guidance based on information you enter.
                </p>
                <p>
                  3.2 Unless we explicitly state otherwise on the Website, the Services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>depend on the accuracy and completeness of the information you provide</li>
                  <li>do not provide tax advice (including inheritance tax planning)</li>
                  <li>do not provide legal advice tailored to your circumstances</li>
                  <li>do not guarantee suitability for complex estates or unusual circumstances</li>
                </ul>
                <p className="mt-4">
                  3.3 You should consider using a solicitor if your circumstances are complex (for example: blended families, substantial estates, business interests, trusts, overseas assets, beneficiaries with disabilities, or likely disputes).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">4. Your responsibilities</h2>
                <p>
                  4.1 You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>providing accurate, complete and up-to-date information</li>
                  <li>carefully reviewing any document we generate before using it</li>
                  <li>ensuring the will reflects your intentions</li>
                  <li>executing your will correctly (see section 7)</li>
                </ul>
                <p className="mt-4">
                  4.2 We are not responsible for errors caused by incorrect or incomplete information you provide.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">5. Prices, VAT, and payment</h2>
                <p>
                  5.1 Prices are shown in GBP and include VAT (unless stated otherwise at checkout).
                </p>
                <p>
                  5.2 Payments are processed by Stripe. We do not store full card details.
                </p>
                <p>
                  5.3 If you purchase add-ons or upgrades (for example, reviews, revisions, storage), the scope of what you receive will be as described on the Website at the time of purchase.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">6. Delivery and access to documents</h2>
                <p>
                  6.1 We provide your documents via the method described on the Website (for example, download, account access, or email).
                </p>
                <p>
                  6.2 You are responsible for downloading/saving copies and keeping them secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">7. Executing and witnessing your will (important)</h2>
                <p>
                  7.1 We may provide general guidance, but you are responsible for ensuring your will is validly executed.
                </p>
                <p>
                  7.2 In England and Wales, a will generally must be:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>in writing</li>
                  <li>signed by you (or by someone in your presence and at your direction)</li>
                  <li>signed in the presence of two witnesses who are present at the same time</li>
                  <li>each witness must then sign in your presence</li>
                </ul>
                <p className="mt-4">
                  7.3 Witnesses (and their spouse/civil partner) should not generally benefit under the will, otherwise gifts to them may fail.
                </p>
                <p>
                  7.4 If you are uncertain about execution, capacity, or whether a will may be challenged, you should obtain independent legal advice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">8. Refunds and corrections</h2>
                <p>
                  8.1 Our policy (in addition to your legal rights): We are happy to provide a full refund or to provide corrections if required.
                </p>
                <p>
                  8.2 Where issues are caused by information you entered incorrectly or incompletely, we will still try to help, but you remain responsible for the final content and execution.
                </p>
                <p>
                  8.3 Nothing in these Terms limits your statutory consumer rights (see section 9).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">9. Consumer cancellation rights (digital services)</h2>
                <p>
                  9.1 If you are a consumer, you may have cancellation rights under the Consumer Contracts Regulations.
                </p>
                <p>
                  9.2 For digital content supplied not on a tangible medium, the right to cancel can be lost once supply begins if you gave the required consent and acknowledgement.
                </p>
                <p>
                  9.3 Regardless of the above, our goodwill refund policy in section 8 applies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">10. Acceptable use</h2>
                <p>
                  You must not:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>misuse the Website (including attempting unauthorised access, introducing malware, or disrupting the service)</li>
                  <li>scrape, reverse engineer, or copy templates/content for resale</li>
                  <li>use the Services for unlawful purposes</li>
                </ul>
                <p className="mt-4">
                  We may suspend or restrict access where we reasonably believe misuse is occurring.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">11. Intellectual property</h2>
                <p>
                  11.1 The Website, branding, templates, and content are owned by or licensed to Willdone.
                </p>
                <p>
                  11.2 Subject to payment, we grant you a personal, non-transferable licence to use your generated documents for your own personal purposes. You may not resell or republish our templates or content.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">12. Privacy and data protection</h2>
                <p>
                  12.1 We process personal data in line with our <Link href="/privacy-policy" className="text-primary hover:underline underline-offset-4 font-medium">Privacy Policy</Link>.
                </p>
                <p>
                  12.2 We use cookies as described in our <Link href="/cookie-policy" className="text-primary hover:underline underline-offset-4 font-medium">Cookie Policy</Link>.
                </p>
                <p>
                  12.3 You must ensure you have permission to provide personal data about others (for example, executors/beneficiaries).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">13. Availability and changes</h2>
                <p>
                  13.1 We aim to keep the Website available but do not guarantee uninterrupted access.
                </p>
                <p>
                  13.2 We may update the Website or Services to improve security, compliance, or performance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">14. Liability</h2>
                <p>
                  14.1 Nothing in these Terms limits or excludes liability where it would be unlawful to do so (including for death/personal injury caused by negligence, or fraud).
                </p>
                <p>
                  14.2 Subject to 14.1, we are not liable for losses arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>incorrect/incomplete information you entered</li>
                  <li>a will being invalid due to incorrect signing/witnessing</li>
                  <li>complex circumstances where you should have sought tailored advice</li>
                  <li>indirect or consequential losses</li>
                </ul>
                <p className="mt-4">
                  14.3 Subject to 14.1, our total liability for claims connected to the Services is limited to the amount you paid for the relevant purchase.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">15. Not a regulated law firm and no legal advice</h2>
                <p>
                  15.1 Willdone is not a firm of solicitors and is not regulated by the Solicitors Regulation Authority (unless we state otherwise on the Website).
                </p>
                <p>
                  15.2 Unless explicitly stated, the Services provide document generation and general information and do not constitute legal advice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">16. Complaints</h2>
                <p>
                  If you have a complaint, email hello@willdone.co.uk with details and your order reference. We aim to respond within [X] business days.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">17. Governing law and jurisdiction</h2>
                <p>
                  These Terms are governed by the laws of England and Wales, and the courts of England and Wales have jurisdiction (subject to any mandatory consumer rights).
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



